import { useAuth } from './auth'

export type OrgRole = 'owner' | 'manager' | 'sender' | 'viewer'

export const ROLE_LABEL: Record<OrgRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  sender: 'Sender',
  viewer: 'Viewer',
}

export const ROLE_DESCRIPTION: Record<OrgRole, string> = {
  owner: 'Can do everything, including billing and managing people.',
  manager: 'Can import, send, remove opt-outs, delete reminders, export history and see billing.',
  sender: 'Can import, send and record opt-outs.',
  viewer: 'Can look but not change anything.',
}

// Roles an owner can give by invite (owner is never granted by an invite).
export const INVITE_ROLES: OrgRole[] = ['manager', 'sender', 'viewer']

export interface TeamMember {
  user_id: string
  email: string
  org_role: OrgRole
  is_active: boolean
  created_at: string
}

export interface TeamInvite {
  invite_id: string
  email: string
  role: OrgRole
  created_at: string
  expires_at: string
  expired: boolean
}

export interface InviteCreateResult {
  invite_id: string
  email: string
  role: OrgRole
  expires_at: string
  link: string
}

export interface ResetLinkResult {
  email: string
  expires_at: string
  link: string
}

export const FORBIDDEN_LINE = 'You can look at this page. Only the account owner can change it.'
export const OWNER_ONLY_HINT = 'Only the account owner can do this.'

/** Who the signed-in person is inside their organisation. */
export function useRole() {
  const { user } = useAuth()
  const role = (user?.org_role ?? 'owner') as OrgRole
  return {
    role,
    isOwner: role === 'owner',
    // owner, manager, sender may import and send; a viewer may not
    canSend: role !== 'viewer',
    // billing screens: owner and manager
    canSeeBilling: role === 'owner' || role === 'manager',
    teamEnabled: user?.feature_team_users === true,
  }
}

/** Per-person label: people are shown by email (there is no name field). */
export function personLabel(email: string): string {
  return email
}

/** "a Sender" / "an Owner" for sentences. */
export function withArticle(label: string): string {
  return /^[AEIOU]/i.test(label) ? `an ${label}` : `a ${label}`
}
